import type { DragEvent } from "react";
/**
 * @module InteractionEngine
 * This is a HTML compatibility helper for the interaction engine.
 * This takes care of handling events originated from HTML elements and
 * calling the relevant callbacks on the SComponents.
 */

import { dragContextStore } from "./dragContext";
import { PointerEvents } from "./interactionObserverConstants";
import { getAvailableDropTargetsAndHoveredNodes } from "./interactionObserver";
import { triggerEvent } from "./interactionTrigger";
import { handleViewportPanningOnDrag } from "../../pixi-components/layers/viewport/viewportPanControl";
import * as PIXI from "pixi.js";
import type { HTMLDragPayload } from "./interactionTypes";
import { getRootNode } from "../renderingEngine/renderer";
import type { SCNode } from "../renderingEngine/fsc.types";
import { renderFlags } from "../layoutEngine/renderFlags";
import type { BoxProps } from "../coreComponents/types";

let lastDragOverTarget: string | null = null;
let lastX: number | null = null;
let lastY: number | null = null;
let rootNode: SCNode | null = null;

function checkPan() {
  if (lastX === null || lastY === null) return;

  handleViewportPanningOnDrag({
    pointerX: lastX,
    pointerY: lastY
  });
}
/**
 * onDragStart is to be handled on HTML Element that is intended
 * to be dragged.
 * This will essentially set the values within DragContextStore.
 */
export function handleHTMLDragStart(
  event: DragEvent<HTMLDivElement>,
  dragPayload: HTMLDragPayload
) {
  dragContextStore.dragType = dragPayload.dragType;
  dragContextStore.subType = dragPayload.subType;
  dragContextStore.extras = dragPayload.extras || {};

  rootNode = getRootNode();
  // Start monitoring the viewport for panning
  PIXI.Ticker.shared.add(checkPan);
}

/**
 * onDragEnd is to be handled on source that was dragged.
 * This will essentially reset the values within DragContextStore.
 */
export function handleHTMLDragEnd(event: DragEvent<HTMLDivElement>) {
  dragContextStore.dragType = null;
  dragContextStore.subType = null;
  PIXI.Ticker.shared.remove(checkPan);
  lastX = null;
  lastY = null;
}

export function handleHTMLDragUpdate(event: DragEvent<HTMLDivElement>) {
  if (event.clientX && event.clientY) {
    /**
     * If you have console open or something, you'll get 0,0 as your
     * mouse move outside the window. We don't want to update the lastX
     * and lastY in that case.
     *
     * And checkPan will continue using the lastX and lastY values.
     */
    lastX = event.clientX;
    lastY = event.clientY;
  }
}

export function handleHTMLDragLeave(event: DragEvent<HTMLDivElement>) {
  if (dragContextStore.dragType && lastDragOverTarget) {
    const eventPayload = {
      type: PointerEvents.DROPPING_OUT,
      dragContextStore
    };
    triggerEvent(lastDragOverTarget, PointerEvents.DROPPING_OUT, eventPayload);
    lastDragOverTarget = null;
  }
}

/**
 * onDragOver is to be handled on the canvas container element.
 * For the specifics of where the item is being dropped, we will
 * trigger the event on the node that is being hovered.
 */
export function handleHTMLDragOver(event: DragEvent<HTMLDivElement>) {
  if (!dragContextStore.dragType) return;

  const { dropTargets, nodesCurrentlyHovered } =
    getAvailableDropTargetsAndHoveredNodes({
      x: event.clientX,
      y: event.clientY,
      dragType: dragContextStore.dragType
    });

  const eventPayload = {
    type: PointerEvents.DROPPING,
    dragContextStore
  };
  let currentDragOverTarget: string | null = null;

  // check if the drop is on the canvas empty area
  const isHoveringOverEmptyCanvasArea =
    nodesCurrentlyHovered.length === 0 ||
    // If the only node hovered is the process node
    (nodesCurrentlyHovered.length === 1 &&
      rootNode?.key === nodesCurrentlyHovered[0].props.key);

  // if there are no nodes hovered or if the node hovered is the process node, then it means the intention of drop is on the canvas. Checking if the process node allows the drop
  let isDropAllowed =
    isHoveringOverEmptyCanvasArea &&
    (rootNode?.props as BoxProps).dropTypes?.includes(
      dragContextStore.dragType
    );

  if (isDropAllowed && lastDragOverTarget) {
    // This is for case when you move out to empty area from a node
    // wherein the lastDragOverTarget is not null
    // and we need to make sure to trigger droppingOut event on the last target
    const eventPayload = {
      type: PointerEvents.DROPPING_OUT,
      dragContextStore
    };
    triggerEvent(lastDragOverTarget, PointerEvents.DROPPING_OUT, eventPayload);

    lastDragOverTarget = null;
    renderFlags.domDirty = true;
  }

  for (const node of dropTargets) {
    const triggerResult = triggerEvent(
      node.props.key,
      PointerEvents.DROPPING,
      eventPayload
    );
    if (triggerResult.handled) {
      // We break loop because we don't want to trigger dropping event on
      // multiple nodes

      // If the node is a valid drop target, we set the drop allowed to true
      isDropAllowed = true;

      // Also, we set the current target to the node that handled the event
      currentDragOverTarget = node.props.key;

      break;
    }
  }
  // If the last target is different from the current target
  // we need to trigger droppingOut event on the last target
  if (lastDragOverTarget && lastDragOverTarget !== currentDragOverTarget) {
    const eventPayload = {
      type: PointerEvents.DROPPING_OUT,
      dragContextStore
    };
    triggerEvent(lastDragOverTarget, PointerEvents.DROPPING_OUT, eventPayload);
  }

  if (isDropAllowed) {
    // Shows the (+) cursor
    event.dataTransfer.dropEffect = "move";
    // Prevent default to allow drop event
    event.preventDefault();
  }
  lastDragOverTarget = currentDragOverTarget;
  renderFlags.domDirty = true;
  isDropAllowed = false;
}

/**
 * onDrop is also handled on the canvas container HTML element.
 * This is where we will trigger the event on the node that is being
 * hovered.
 */
export function handleHTMLDrop(event: DragEvent<HTMLDivElement>) {
  if (!dragContextStore.dragType) return;
  // if the drop is on the canvas empty area

  const { dropTargets, nodesCurrentlyHovered } =
    getAvailableDropTargetsAndHoveredNodes({
      x: event.clientX,
      y: event.clientY,
      dragType: dragContextStore.dragType
    });

  const isHoveringOverEmptyCanvasArea =
    nodesCurrentlyHovered.length === 0 ||
    // If the only node hovered is the process node
    (nodesCurrentlyHovered.length === 1 &&
      rootNode?.key === nodesCurrentlyHovered[0].props.key);

  if (isHoveringOverEmptyCanvasArea) {
    if (!rootNode) return;

    const eventPayload = {
      type: PointerEvents.DROPPED,
      dragContextStore
    };

    const triggerResult = triggerEvent(
      rootNode.key,
      PointerEvents.DROPPED,
      eventPayload
    );

    if (triggerResult.handled) {
      event.preventDefault();
    }

    return;
  }

  const eventPayload = {
    type: PointerEvents.DROPPED,
    dragContextStore
  };
  for (const node of dropTargets) {
    const triggerResult = triggerEvent(
      node.props.key,
      PointerEvents.DROPPED,
      eventPayload
    );
    if (triggerResult.handled) {
      // We break loop because we don't want to trigger dropping event on
      // multiple nodes

      event.preventDefault();
      break;
    }
  }
  renderFlags.domDirty = true;
}
